import React, { useState, useMemo } from 'react';
import {
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CurrencyEuroIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  ChevronDownIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  PencilIcon,
  PlusIcon,
  CheckIcon,
  TagIcon,
  EyeIcon,
  UserGroupIcon,
  BoltIcon,
  BeakerIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  EllipsisHorizontalIcon,
  BuildingLibraryIcon,
  HeartIcon,
  UserPlusIcon,
  CreditCardIcon,
  HomeIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  TruckIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';

const iconMap: { [key: string]: React.ComponentType<any> } = {
  FaUsers: UserGroupIcon, FaBolt: BoltIcon, FaTint: BeakerIcon,
  FaUsersCog: Cog6ToothIcon, FaTools: WrenchScrewdriverIcon,
  FaShieldAlt: ShieldCheckIcon, FaEllipsisH: EllipsisHorizontalIcon,
  FaEuroSign: CurrencyEuroIcon, FaBuilding: BuildingLibraryIcon,
  FaTrash: TagIcon, FaHeart: HeartIcon, FaUserPlus: UserPlusIcon,
  FaMoneyCheckAlt: CreditCardIcon, FaUtensils: HomeIcon,
  FaExchangeAlt: ArrowsRightLeftIcon, FaMoneyBill: BanknotesIcon,
  FaDog: HeartIcon, FaCar: TruckIcon, FaBookOpen: BookOpenIcon,
  default: TagIcon
};

interface Category { id: number; name: string; icon: string; }
interface BudgetData { [categoryName: string]: { income: string | number; expense: string | number; comment?: string; }; }
interface BudgetPlan { id: number; name: string; year: number; importYear: number; budgetData: BudgetData; }
interface BudgetDetailModalProps {
  plan: BudgetPlan;
  categories: Category[];
  accountStatements: any[];
  onClose: () => void;
  onUpdate: (updatedPlan: BudgetPlan) => void;
}

const BudgetDetailModal: React.FC<BudgetDetailModalProps> = ({
  plan, categories, accountStatements, onClose, onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'edit'>('overview');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  const [editName, setEditName] = useState(plan.name);
  const [editYear, setEditYear] = useState(plan.year);
  const [editBudgetData, setEditBudgetData] = useState<BudgetData>(() => {
    const data: BudgetData = {};
    Object.entries(plan.budgetData).forEach(([key, val]) => {
      data[key] = { income: val.income, expense: val.expense, comment: val.comment || '' };
    });
    return data;
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [showAddCategory, setShowAddCategory] = useState(false);

  // Shorthand for fullscreen sizes
  const fs = isFullscreen;

  React.useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    document.body.style.overflow = 'hidden';
    return () => { clearTimeout(timer); document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => { setIsVisible(false); setTimeout(onClose, 200); };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

  const istValues = useMemo(() => {
    const yearFiltered = accountStatements.filter(stmt => {
      const bookingYear = new Date(stmt.buchungstag).getFullYear();
      return bookingYear === plan.year && stmt.konto_id && !stmt.von_konto_id && !stmt.nach_konto_id;
    });
    let totalIstIncome = 0;
    let totalIstExpense = 0;
    const categoryIst = new Map<string, { income: number; expense: number }>();
    yearFiltered.forEach(stmt => {
      if (stmt.categories) {
        stmt.categories.forEach((cat: any) => {
          const category = categories.find(c => c.id === cat.kategorie_id);
          if (category) {
            if (!categoryIst.has(category.name)) categoryIst.set(category.name, { income: 0, expense: 0 });
            const existing = categoryIst.get(category.name)!;
            if (stmt.betrag > 0) { existing.income += cat.betrag; totalIstIncome += cat.betrag; }
            else { existing.expense -= cat.betrag; totalIstExpense -= cat.betrag; }
          }
        });
      }
    });
    return { totalIstIncome, totalIstExpense, categoryIst, yearFiltered };
  }, [accountStatements, plan.year, categories]);

  const sollValues = useMemo(() => {
    let income = 0, expense = 0;
    Object.values(plan.budgetData).forEach(data => {
      income += typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
      expense += typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
    });
    return { income, expense, net: income - expense };
  }, [plan.budgetData]);

  const editSums = useMemo(() => {
    let income = 0, expense = 0;
    Object.values(editBudgetData).forEach(data => {
      income += parseFloat(data.income as string) || 0;
      expense += parseFloat(data.expense as string) || 0;
    });
    return { income, expense, net: income - expense };
  }, [editBudgetData]);

  const handleEditChange = (categoryName: string, field: 'income' | 'expense' | 'comment', value: string) => {
    setEditBudgetData(prev => ({ ...prev, [categoryName]: { ...prev[categoryName], [field]: value } }));
    setHasChanges(true);
  };
  const handleRemoveCategory = (categoryName: string) => {
    setEditBudgetData(prev => { const d = { ...prev }; delete d[categoryName]; return d; });
    setHasChanges(true);
  };
  const handleAddCategory = () => {
    if (!newCategoryName.trim() || editBudgetData[newCategoryName.trim()]) return;
    setEditBudgetData(prev => ({ ...prev, [newCategoryName.trim()]: { income: '', expense: '', comment: '' } }));
    setNewCategoryName(''); setShowAddCategory(false); setHasChanges(true);
  };
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiClient.put(`/api/finance/budget-plans/${plan.id}`, { name: editName, year: editYear, budgetData: editBudgetData });
      onUpdate({ ...plan, name: editName, year: editYear, budgetData: editBudgetData });
      setHasChanges(false);
    } catch (err) { console.error('Error saving budget plan:', err); }
    finally { setIsSaving(false); }
  };
  const getIcon = (categoryName: string) => {
    const cat = categories.find(c => c.name === categoryName);
    return cat?.icon ? (iconMap[cat.icon] || iconMap.default) : iconMap.default;
  };

  // === RENDER ===
  return (
    <div
      className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm ${isFullscreen ? '' : 'p-0 sm:p-4'}`}
      onClick={handleClose}
    >
      <div
        className={`bg-white dark:bg-[#1c2432] flex flex-col shadow-2xl animate-slide-up sm:animate-fade-in ${
          isFullscreen
            ? 'w-full h-full max-h-full rounded-none border-0'
            : 'w-full sm:max-w-5xl max-h-[90vh] rounded-t-2xl sm:rounded-2xl border-t sm:border border-gray-200 dark:border-gray-700'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10 ${fs ? 'px-10 py-5' : 'p-4'}`}>
          <div className="flex items-center gap-3 min-w-0">
            <h3 className={`font-bold text-gray-900 dark:text-white truncate ${fs ? 'text-3xl' : 'text-lg'}`}>
              {plan.name}
            </h3>
            <span className={`inline-flex items-center gap-1 rounded-full font-medium bg-info-100 dark:bg-info-900/30 text-info-800 dark:text-info-200 flex-shrink-0 ${fs ? 'px-4 py-1.5 text-base' : 'px-2 py-0.5 text-xs'}`}>
              <CalendarIcon className={fs ? 'h-5 w-5' : 'h-3 w-3'} />
              {plan.year}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <div className={`hidden sm:flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 mr-2`}>
              <button
                onClick={() => setActiveTab('overview')}
                className={`flex items-center gap-1.5 rounded-md font-medium transition-colors ${fs ? 'px-5 py-2.5 text-base' : 'px-3 py-1.5 text-xs'} ${
                  activeTab === 'overview'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <EyeIcon className={fs ? 'h-5 w-5' : 'h-3.5 w-3.5'} />
                Übersicht
              </button>
              <button
                onClick={() => setActiveTab('edit')}
                className={`flex items-center gap-1.5 rounded-md font-medium transition-colors ${fs ? 'px-5 py-2.5 text-base' : 'px-3 py-1.5 text-xs'} ${
                  activeTab === 'edit'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                }`}
              >
                <PencilIcon className={fs ? 'h-5 w-5' : 'h-3.5 w-3.5'} />
                Bearbeiten
                {hasChanges && <span className="w-2 h-2 bg-warning-500 rounded-full" />}
              </button>
            </div>
            <button onClick={() => setIsFullscreen(!isFullscreen)} className={`hidden sm:block text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors ${fs ? 'p-3' : 'p-2'}`}>
              {isFullscreen ? <ArrowsPointingInIcon className={fs ? 'h-7 w-7' : 'h-5 w-5'} /> : <ArrowsPointingOutIcon className="h-5 w-5" />}
            </button>
            <button onClick={handleClose} className={`text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ${fs ? 'p-3' : 'p-2'}`}>
              <XMarkIcon className={fs ? 'h-7 w-7' : 'h-5 w-5'} />
            </button>
          </div>
        </div>

        {/* Mobile Tab Switcher */}
        <div className="sm:hidden flex border-b border-gray-200 dark:border-gray-700">
          <button onClick={() => setActiveTab('overview')} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'overview' ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600' : 'text-gray-500 dark:text-gray-400'}`}>
            <EyeIcon className="h-4 w-4" /> Übersicht
          </button>
          <button onClick={() => setActiveTab('edit')} className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors ${activeTab === 'edit' ? 'text-primary-600 dark:text-primary-400 border-b-2 border-primary-600' : 'text-gray-500 dark:text-gray-400'}`}>
            <PencilIcon className="h-4 w-4" /> Bearbeiten
            {hasChanges && <span className="w-1.5 h-1.5 bg-warning-500 rounded-full ml-1" />}
          </button>
        </div>

        {/* Content */}
        <div className={`overflow-y-auto ${fs ? 'px-10 py-8' : 'p-4'}`} style={{ maxHeight: fs ? 'calc(100vh - 80px)' : 'calc(92vh - 57px)' }}>

          {/* === TAB: ÜBERSICHT === */}
          {activeTab === 'overview' && (() => {
            const istNet = istValues.totalIstIncome + istValues.totalIstExpense;
            const netDelta = istNet - sollValues.net;
            const incPct = sollValues.income > 0 ? Math.round((istValues.totalIstIncome / sollValues.income) * 100) : 0;
            const expPct = sollValues.expense > 0 ? Math.round((Math.abs(istValues.totalIstExpense) / sollValues.expense) * 100) : 0;

            return (
              <div className={fs ? 'space-y-10' : 'space-y-6'}>

                {/* === Gesamtübersicht Karten === */}
                <div className={`grid grid-cols-1 sm:grid-cols-3 ${fs ? 'gap-6' : 'gap-3'}`}>
                  {/* Einnahmen */}
                  <div className={`bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 ${fs ? 'p-8' : 'p-4'}`}>
                    <div className={`flex items-center justify-between ${fs ? 'mb-5' : 'mb-3'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`bg-success-500 rounded-lg ${fs ? 'p-3' : 'p-1.5'}`}>
                          <ArrowTrendingUpIcon className={`text-white ${fs ? 'h-7 w-7' : 'h-4 w-4'}`} />
                        </div>
                        <span className={`font-medium text-gray-600 dark:text-gray-400 ${fs ? 'text-xl' : 'text-sm'}`}>Einnahmen</span>
                      </div>
                      <span className={`inline-flex items-center rounded font-bold tabular-nums ${fs ? 'px-3 py-1 text-base' : 'px-1.5 py-0.5 text-[11px]'} ${
                        incPct >= 100
                          ? 'text-success-700 dark:text-success-400 bg-success-50 dark:bg-success-900/20'
                          : 'text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20'
                      }`}>{incPct}%</span>
                    </div>
                    <p className={`font-bold text-gray-900 dark:text-white tabular-nums ${fs ? 'text-5xl mb-3' : 'text-2xl mb-1'}`}>
                      {formatCurrency(istValues.totalIstIncome)}
                    </p>
                    <div className={fs ? 'mb-3' : 'mb-2'}>
                      <div className={`w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden ${fs ? 'h-4' : 'h-2'}`}>
                        <div className={`${fs ? 'h-4' : 'h-2'} rounded-full transition-all duration-500 ${incPct > 100 ? 'bg-success-600' : 'bg-success-500'}`} style={{ width: `${Math.min(incPct, 100)}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-gray-500 dark:text-gray-400 ${fs ? 'text-base' : 'text-xs'}`}>
                        von {formatCurrency(sollValues.income)} geplant
                      </span>
                      {sollValues.income > 0 && (
                        <span className={`font-semibold tabular-nums ${fs ? 'text-base' : 'text-xs'} ${istValues.totalIstIncome >= sollValues.income ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400'}`}>
                          {istValues.totalIstIncome >= sollValues.income ? '+' : ''}{formatCurrency(istValues.totalIstIncome - sollValues.income)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Ausgaben */}
                  <div className={`bg-white dark:bg-[#1c2432] rounded-xl border border-gray-200 dark:border-gray-700 ${fs ? 'p-8' : 'p-4'}`}>
                    <div className={`flex items-center justify-between ${fs ? 'mb-5' : 'mb-3'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`bg-error-500 rounded-lg ${fs ? 'p-3' : 'p-1.5'}`}>
                          <ArrowTrendingDownIcon className={`text-white ${fs ? 'h-7 w-7' : 'h-4 w-4'}`} />
                        </div>
                        <span className={`font-medium text-gray-600 dark:text-gray-400 ${fs ? 'text-xl' : 'text-sm'}`}>Ausgaben</span>
                      </div>
                      <span className={`inline-flex items-center rounded font-bold tabular-nums ${fs ? 'px-3 py-1 text-base' : 'px-1.5 py-0.5 text-[11px]'} ${
                        expPct <= 100
                          ? 'text-success-700 dark:text-success-400 bg-success-50 dark:bg-success-900/20'
                          : 'text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20'
                      }`}>{expPct}%</span>
                    </div>
                    <p className={`font-bold text-gray-900 dark:text-white tabular-nums ${fs ? 'text-5xl mb-3' : 'text-2xl mb-1'}`}>
                      {formatCurrency(Math.abs(istValues.totalIstExpense))}
                    </p>
                    <div className={fs ? 'mb-3' : 'mb-2'}>
                      <div className={`w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden ${fs ? 'h-4' : 'h-2'}`}>
                        <div className={`${fs ? 'h-4' : 'h-2'} rounded-full transition-all duration-500 ${expPct > 100 ? 'bg-error-600' : 'bg-error-400'}`} style={{ width: `${Math.min(expPct, 100)}%` }} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-gray-500 dark:text-gray-400 ${fs ? 'text-base' : 'text-xs'}`}>
                        von {formatCurrency(sollValues.expense)} geplant
                      </span>
                      {sollValues.expense > 0 && (
                        <span className={`font-semibold tabular-nums ${fs ? 'text-base' : 'text-xs'} ${Math.abs(istValues.totalIstExpense) <= sollValues.expense ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400'}`}>
                          {Math.abs(istValues.totalIstExpense) > sollValues.expense ? '+' : ''}{formatCurrency(Math.abs(istValues.totalIstExpense) - sollValues.expense)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Bilanz */}
                  <div className={`bg-white dark:bg-[#1c2432] rounded-xl border-2 ${fs ? 'p-8' : 'p-4'} ${
                    netDelta >= 0 ? 'border-success-300 dark:border-success-700' : 'border-warning-300 dark:border-warning-700'
                  }`}>
                    <div className={`flex items-center justify-between ${fs ? 'mb-5' : 'mb-3'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`rounded-lg ${fs ? 'p-3' : 'p-1.5'} ${netDelta >= 0 ? 'bg-success-500' : 'bg-warning-500'}`}>
                          <CurrencyEuroIcon className={`text-white ${fs ? 'h-7 w-7' : 'h-4 w-4'}`} />
                        </div>
                        <span className={`font-medium text-gray-600 dark:text-gray-400 ${fs ? 'text-xl' : 'text-sm'}`}>Bilanz</span>
                      </div>
                      {netDelta >= 0
                        ? <CheckCircleIcon className={`text-success-500 ${fs ? 'h-8 w-8' : 'h-5 w-5'}`} />
                        : <ExclamationTriangleIcon className={`text-warning-500 ${fs ? 'h-8 w-8' : 'h-5 w-5'}`} />
                      }
                    </div>
                    <p className={`font-bold tabular-nums ${fs ? 'text-5xl mb-4' : 'text-2xl mb-1'} ${istNet >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                      {istNet >= 0 ? '+' : ''}{formatCurrency(istNet)}
                    </p>
                    <div className={`flex items-center gap-2 rounded-lg mt-2 ${fs ? 'px-4 py-3' : 'px-2.5 py-1.5'} ${
                      netDelta >= 0 ? 'bg-success-50 dark:bg-success-900/20' : 'bg-warning-50 dark:bg-warning-900/20'
                    }`}>
                      <span className={`font-semibold tabular-nums ${fs ? 'text-lg' : 'text-xs'} ${netDelta >= 0 ? 'text-success-700 dark:text-success-300' : 'text-warning-700 dark:text-warning-300'}`}>
                        {netDelta >= 0 ? '+' : ''}{formatCurrency(netDelta)}
                      </span>
                      <span className={`${fs ? 'text-lg' : 'text-xs'} ${netDelta >= 0 ? 'text-success-600 dark:text-success-400' : 'text-warning-600 dark:text-warning-400'}`}>
                        {netDelta >= 0 ? 'über' : 'unter'} Plan ({formatCurrency(sollValues.net)})
                      </span>
                    </div>
                  </div>
                </div>

                {/* === Fortschrittsindikatoren === */}
                <div className={`grid grid-cols-2 ${fs ? 'gap-6' : 'gap-3'}`}>
                  <div className={`bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 ${fs ? 'p-6' : 'p-3'}`}>
                    <div className={`flex items-center justify-between ${fs ? 'mb-3' : 'mb-1'}`}>
                      <span className={`font-medium text-gray-500 dark:text-gray-400 ${fs ? 'text-lg' : 'text-xs'}`}>Einnahmen-Erreichung</span>
                      <span className={`font-bold tabular-nums ${fs ? 'text-lg' : 'text-xs'} ${incPct >= 100 ? 'text-success-600 dark:text-success-400' : 'text-gray-700 dark:text-gray-300'}`}>{incPct}%</span>
                    </div>
                    <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${fs ? 'h-6' : 'h-3'}`}>
                      <div className={`${fs ? 'h-6' : 'h-3'} rounded-full transition-all duration-700 ${incPct >= 100 ? 'bg-success-500' : incPct >= 70 ? 'bg-success-400' : incPct >= 40 ? 'bg-warning-400' : 'bg-error-400'}`} style={{ width: `${Math.min(incPct, 100)}%` }} />
                    </div>
                  </div>
                  <div className={`bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 ${fs ? 'p-6' : 'p-3'}`}>
                    <div className={`flex items-center justify-between ${fs ? 'mb-3' : 'mb-1'}`}>
                      <span className={`font-medium text-gray-500 dark:text-gray-400 ${fs ? 'text-lg' : 'text-xs'}`}>Budget-Auslastung</span>
                      <span className={`font-bold tabular-nums ${fs ? 'text-lg' : 'text-xs'} ${expPct > 100 ? 'text-error-600 dark:text-error-400' : 'text-gray-700 dark:text-gray-300'}`}>{expPct}%</span>
                    </div>
                    <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden ${fs ? 'h-6' : 'h-3'}`}>
                      <div className={`${fs ? 'h-6' : 'h-3'} rounded-full transition-all duration-700 ${expPct > 100 ? 'bg-error-500' : expPct >= 80 ? 'bg-warning-400' : 'bg-success-400'}`} style={{ width: `${Math.min(expPct, 100)}%` }} />
                    </div>
                  </div>
                </div>

                {/* === Buchhalterische Bewertung === */}
                {(() => {
                  const isPositive = netDelta >= 0;
                  return (
                    <div className={`rounded-xl border ${fs ? 'p-8' : 'p-4'} ${isPositive ? 'bg-success-50 dark:bg-success-900/10 border-success-200 dark:border-success-800' : 'bg-warning-50 dark:bg-warning-900/10 border-warning-200 dark:border-warning-800'}`}>
                      <div className={`flex items-start ${fs ? 'gap-5' : 'gap-3'}`}>
                        {isPositive
                          ? <CheckCircleIcon className={`text-success-600 dark:text-success-400 flex-shrink-0 mt-0.5 ${fs ? 'h-8 w-8' : 'h-5 w-5'}`} />
                          : <ExclamationTriangleIcon className={`text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5 ${fs ? 'h-8 w-8' : 'h-5 w-5'}`} />
                        }
                        <div>
                          <p className={`font-semibold mb-0.5 ${fs ? 'text-xl' : 'text-sm'} ${isPositive ? 'text-success-900 dark:text-success-100' : 'text-warning-900 dark:text-warning-100'}`}>
                            {isPositive ? 'Positive Entwicklung' : 'Planabweichung festgestellt'}
                          </p>
                          <p className={`${fs ? 'text-lg leading-relaxed' : 'text-sm'} ${isPositive ? 'text-success-700 dark:text-success-300' : 'text-warning-700 dark:text-warning-300'}`}>
                            {isPositive
                              ? `Das Gesamtergebnis liegt um ${formatCurrency(Math.abs(netDelta))} über der Planung. Einnahmen sind zu ${incPct}% erreicht, Budget zu ${expPct}% ausgelastet.`
                              : `Das Gesamtergebnis liegt um ${formatCurrency(Math.abs(netDelta))} unter der Planung. Einnahmen sind zu ${incPct}% erreicht, Budget zu ${expPct}% ausgelastet. Prüfung empfohlen.`
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* === Kategorie-Übersicht === */}
                <div>
                  <h4 className={`font-semibold text-gray-900 dark:text-white ${fs ? 'text-2xl mb-5' : 'text-sm mb-3'}`}>
                    Kategorien
                  </h4>
                  <div className={fs ? 'space-y-4' : 'space-y-2'}>
                    {Object.entries(plan.budgetData)
                      .filter(([catName, data]) => {
                        const si = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
                        const se = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
                        const ci = istValues.categoryIst.get(catName);
                        return si > 0 || se > 0 || (ci && (ci.income > 0 || ci.expense !== 0));
                      })
                      .sort(([, aData], [, bData]) => {
                        const aTotal = (typeof aData.income === 'string' ? parseFloat(aData.income) || 0 : aData.income || 0)
                          + (typeof aData.expense === 'string' ? parseFloat(aData.expense) || 0 : aData.expense || 0);
                        const bTotal = (typeof bData.income === 'string' ? parseFloat(bData.income) || 0 : bData.income || 0)
                          + (typeof bData.expense === 'string' ? parseFloat(bData.expense) || 0 : bData.expense || 0);
                        return bTotal - aTotal;
                      })
                      .map(([categoryName, data]) => {
                        const sollIncome = typeof data.income === 'string' ? parseFloat(data.income) || 0 : data.income || 0;
                        const sollExpense = typeof data.expense === 'string' ? parseFloat(data.expense) || 0 : data.expense || 0;
                        const ci = istValues.categoryIst.get(categoryName);
                        const catIstIncome = ci?.income || 0;
                        const catIstExpense = ci?.expense || 0;
                        const catIstNet = catIstIncome + catIstExpense;
                        const IconComponent = getIcon(categoryName);
                        const showIncome = sollIncome > 0 || catIstIncome > 0;
                        const showExpense = sollExpense > 0 || catIstExpense !== 0;
                        const isExpanded = expandedCategory === categoryName;
                        const categoryBookings = istValues.yearFiltered.filter(stmt =>
                          stmt.categories?.some((cat: any) => categories.find(c => c.id === cat.kategorie_id)?.name === categoryName)
                        );
                        const catIncPct = sollIncome > 0 ? Math.round((catIstIncome / sollIncome) * 100) : null;
                        const catExpPct = sollExpense > 0 ? Math.round((Math.abs(catIstExpense) / sollExpense) * 100) : null;

                        return (
                          <div key={categoryName}>
                            <div
                              onClick={() => setExpandedCategory(isExpanded ? null : categoryName)}
                              className={`bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 cursor-pointer transition-all hover:shadow-sm ${
                                fs ? 'p-6' : 'p-3 sm:p-4'
                              } ${isExpanded ? 'rounded-t-xl border-b-0' : 'rounded-xl'}`}
                            >
                              {/* Header */}
                              <div className={`flex items-center justify-between ${fs ? 'mb-5' : 'mb-2.5'}`}>
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`bg-primary-100 dark:bg-primary-900/30 rounded-lg flex-shrink-0 ${fs ? 'p-3' : 'p-1.5'}`}>
                                    <IconComponent className={`text-primary-600 dark:text-primary-400 ${fs ? 'h-7 w-7' : 'h-4 w-4'}`} />
                                  </div>
                                  <span className={`font-semibold text-gray-900 dark:text-white truncate ${fs ? 'text-2xl' : 'text-sm'}`}>{categoryName}</span>
                                  {categoryBookings.length > 0 && (
                                    <span className={`bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-full flex-shrink-0 ${fs ? 'text-base px-3 py-1' : 'text-[10px] px-1.5 py-0.5'}`}>
                                      {categoryBookings.length}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`font-bold tabular-nums ${fs ? 'text-2xl' : 'text-sm'} ${catIstNet >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                                    {catIstNet >= 0 ? '+' : ''}{formatCurrency(catIstNet)}
                                  </span>
                                  <ChevronDownIcon className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''} ${fs ? 'h-6 w-6' : 'h-4 w-4'}`} />
                                </div>
                              </div>

                              {/* Kommentar */}
                              {data.comment && (
                                <p className={`text-gray-500 dark:text-gray-400 italic ${fs ? 'text-base mb-4 -mt-2' : 'text-xs mb-1.5 -mt-1'}`}>
                                  {data.comment}
                                </p>
                              )}

                              {/* Progress rows */}
                              <div className={fs ? 'space-y-4' : 'space-y-2'}>
                                {showIncome && (
                                  <div>
                                    <div className={`flex items-center justify-between ${fs ? 'mb-2' : 'mb-1'}`}>
                                      <span className={`font-medium text-success-600 dark:text-success-400 ${fs ? 'text-base' : 'text-[11px]'}`}>Einnahmen</span>
                                      <div className={`flex items-center ${fs ? 'gap-4' : 'gap-2'}`}>
                                        <span className={`text-gray-500 dark:text-gray-400 tabular-nums ${fs ? 'text-base' : 'text-[11px]'}`}>
                                          {formatCurrency(catIstIncome)} <span className="text-gray-400 dark:text-gray-500">/</span> {formatCurrency(sollIncome)}
                                        </span>
                                        {catIncPct !== null && (
                                          <span className={`inline-flex items-center rounded font-bold tabular-nums ${fs ? 'px-3 py-1 text-base' : 'px-1.5 py-0.5 text-[11px]'} ${
                                            catIncPct >= 100
                                              ? 'text-success-700 dark:text-success-400 bg-success-50 dark:bg-success-900/20'
                                              : 'text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20'
                                          }`}>{catIncPct}%</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={`w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden ${fs ? 'h-3' : 'h-2'}`}>
                                      <div className={`${fs ? 'h-3' : 'h-2'} rounded-full transition-all duration-500 ${(catIncPct ?? 0) > 100 ? 'bg-success-600' : 'bg-success-400'}`}
                                        style={{ width: `${Math.min(catIncPct ?? (catIstIncome > 0 ? 100 : 0), 100)}%` }} />
                                    </div>
                                  </div>
                                )}
                                {showExpense && (
                                  <div>
                                    <div className={`flex items-center justify-between ${fs ? 'mb-2' : 'mb-1'}`}>
                                      <span className={`font-medium text-error-600 dark:text-error-400 ${fs ? 'text-base' : 'text-[11px]'}`}>Ausgaben</span>
                                      <div className={`flex items-center ${fs ? 'gap-4' : 'gap-2'}`}>
                                        <span className={`text-gray-500 dark:text-gray-400 tabular-nums ${fs ? 'text-base' : 'text-[11px]'}`}>
                                          {formatCurrency(Math.abs(catIstExpense))} <span className="text-gray-400 dark:text-gray-500">/</span> {formatCurrency(sollExpense)}
                                        </span>
                                        {catExpPct !== null && (
                                          <span className={`inline-flex items-center rounded font-bold tabular-nums ${fs ? 'px-3 py-1 text-base' : 'px-1.5 py-0.5 text-[11px]'} ${
                                            catExpPct <= 100
                                              ? 'text-success-700 dark:text-success-400 bg-success-50 dark:bg-success-900/20'
                                              : 'text-warning-700 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/20'
                                          }`}>{catExpPct}%</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className={`w-full bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden ${fs ? 'h-3' : 'h-2'}`}>
                                      <div className={`${fs ? 'h-3' : 'h-2'} rounded-full transition-all duration-500 ${(catExpPct ?? 0) > 100 ? 'bg-error-500' : 'bg-error-300 dark:bg-error-400'}`}
                                        style={{ width: `${Math.min(catExpPct ?? (Math.abs(catIstExpense) > 0 ? 100 : 0), 100)}%` }} />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Expandable Buchungen */}
                            {isExpanded && (
                              <div className={`bg-gray-50 dark:bg-gray-900/50 rounded-b-xl border border-t-0 border-gray-200 dark:border-gray-700 ${fs ? 'p-6' : 'p-3'}`}>
                                {categoryBookings.length > 0 ? (
                                  <div className={fs ? 'space-y-3' : 'space-y-1.5'}>
                                    {categoryBookings.map((booking: any) => {
                                      const cats = booking.categories?.filter((cat: any) =>
                                        categories.find(c => c.id === cat.kategorie_id)?.name === categoryName
                                      ) || [];
                                      return cats.map((cat: any, idx: number) => {
                                        const sign = parseFloat(booking.betrag) >= 0 ? 1 : -1;
                                        const amount = parseFloat(cat.betrag) * sign;
                                        return (
                                          <div key={`${booking.id}-${idx}`} className={`flex items-center justify-between gap-3 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors ${fs ? 'py-3 px-4' : 'py-1.5 px-2'}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                              <span className={`text-gray-400 dark:text-gray-500 flex-shrink-0 tabular-nums ${fs ? 'text-base w-20' : 'text-[11px] w-16'}`}>
                                                {new Date(booking.buchungstag).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                                              </span>
                                              <span className={`text-gray-700 dark:text-gray-300 truncate ${fs ? 'text-base' : 'text-xs'}`}>
                                                {booking.verwendungszweck || booking.buchungstext || 'Keine Beschreibung'}
                                              </span>
                                            </div>
                                            <span className={`font-bold flex-shrink-0 tabular-nums ${fs ? 'text-lg' : 'text-xs'} ${amount >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'}`}>
                                              {amount >= 0 ? '+' : ''}{formatCurrency(amount)}
                                            </span>
                                          </div>
                                        );
                                      });
                                    })}
                                  </div>
                                ) : (
                                  <p className={`text-gray-400 dark:text-gray-500 text-center ${fs ? 'text-base py-4' : 'text-xs py-2'}`}>
                                    Keine Buchungen in dieser Kategorie
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* === TAB: BEARBEITEN === */}
          {activeTab === 'edit' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Name der Planung</label>
                  <input type="text" value={editName} onChange={(e) => { setEditName(e.target.value); setHasChanges(true); }}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Planungsjahr</label>
                  <input type="number" value={editYear} onChange={(e) => { setEditYear(parseInt(e.target.value)); setHasChanges(true); }}
                    className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm" min="2020" max="2035" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white dark:bg-[#1c2432] rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1 bg-success-500 rounded-lg"><ArrowTrendingUpIcon className="h-3.5 w-3.5 text-white" /></div>
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Einnahmen</p>
                  </div>
                  <p className="text-lg sm:text-xl font-bold text-success-600 dark:text-success-400 tabular-nums">{formatCurrency(editSums.income)}</p>
                </div>
                <div className="bg-white dark:bg-[#1c2432] rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="p-1 bg-error-500 rounded-lg"><ArrowTrendingDownIcon className="h-3.5 w-3.5 text-white" /></div>
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Ausgaben</p>
                  </div>
                  <p className="text-lg sm:text-xl font-bold text-error-600 dark:text-error-400 tabular-nums">{formatCurrency(editSums.expense)}</p>
                </div>
                <div className="bg-white dark:bg-[#1c2432] rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className={`p-1 rounded-lg ${editSums.net >= 0 ? 'bg-info-500' : 'bg-warning-500'}`}><CurrencyEuroIcon className="h-3.5 w-3.5 text-white" /></div>
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Bilanz</p>
                  </div>
                  <p className={`text-lg sm:text-xl font-bold tabular-nums ${editSums.net >= 0 ? 'text-info-600 dark:text-info-400' : 'text-warning-600 dark:text-warning-400'}`}>
                    {editSums.net >= 0 ? '+' : ''}{formatCurrency(editSums.net)}
                  </p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Kategorien ({Object.keys(editBudgetData).length})</h4>
                  <button onClick={() => setShowAddCategory(!showAddCategory)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                    <PlusIcon className="h-3.5 w-3.5" /> Kategorie
                  </button>
                </div>
                {showAddCategory && (
                  <div className="flex items-center gap-2 mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setShowAddCategory(false); }}
                      placeholder="Kategorie-Name..." className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white" autoFocus />
                    <button onClick={handleAddCategory} disabled={!newCategoryName.trim()} className="px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors">Hinzufügen</button>
                    <button onClick={() => { setShowAddCategory(false); setNewCategoryName(''); }} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><XMarkIcon className="h-4 w-4" /></button>
                  </div>
                )}
                <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                  {Object.entries(editBudgetData).map(([categoryName, data]) => {
                    const IconComponent = getIcon(categoryName);
                    const net = (parseFloat(data.income as string) || 0) - (parseFloat(data.expense as string) || 0);
                    return (
                      <div key={categoryName} className="p-3 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-gray-700 rounded-lg space-y-2">
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 sm:gap-3">
                          <div className="flex items-center gap-2 sm:col-span-1">
                            <div className="p-1 bg-primary-100 dark:bg-primary-900/30 rounded-lg flex-shrink-0"><IconComponent className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" /></div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{categoryName}</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-0">
                            <div>
                              <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 font-medium sm:block hidden">Einnahmen (€)</label>
                              <input type="number" step="0.01" value={data.income} onChange={(e) => handleEditChange(categoryName, 'income', e.target.value)}
                                className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white" placeholder="Einnahmen" />
                            </div>
                            <div className="sm:hidden">
                              <input type="number" step="0.01" value={data.expense} onChange={(e) => handleEditChange(categoryName, 'expense', e.target.value)}
                                className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white" placeholder="Ausgaben" />
                            </div>
                          </div>
                          <div className="hidden sm:block">
                            <label className="block text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 font-medium">Ausgaben (€)</label>
                            <input type="number" step="0.01" value={data.expense} onChange={(e) => handleEditChange(categoryName, 'expense', e.target.value)}
                              className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white" placeholder="0.00" />
                          </div>
                          <div className="flex items-end justify-between sm:justify-end gap-2">
                            <div className={`px-2 py-2 text-sm font-medium rounded-lg ${net >= 0 ? 'text-success-600 dark:text-success-400 bg-success-50 dark:bg-success-900/20' : 'text-error-600 dark:text-error-400 bg-error-50 dark:bg-error-900/20'}`}>
                              {formatCurrency(net)}
                            </div>
                            <button onClick={() => handleRemoveCategory(categoryName)} className="p-2 text-gray-400 hover:text-error-600 transition-colors flex-shrink-0"><XMarkIcon className="h-4 w-4" /></button>
                          </div>
                        </div>
                        <input
                          type="text"
                          value={data.comment || ''}
                          onChange={(e) => handleEditChange(categoryName, 'comment', e.target.value)}
                          className="w-full px-2.5 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-gray-500 dark:text-gray-400 italic"
                          placeholder="Kommentar zur Kategorie (optional)..."
                        />
                      </div>
                    );
                  })}
                </div>
                {Object.keys(editBudgetData).length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <TagIcon className="h-10 w-10 mx-auto mb-2 text-gray-300" /><p className="text-sm">Keine Kategorien vorhanden</p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 -mx-4 px-4 py-3 flex items-center justify-between">
                {hasChanges ? (
                  <span className="text-xs text-warning-600 dark:text-warning-400 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-warning-500 rounded-full" /> Ungespeicherte Änderungen
                  </span>
                ) : <span />}
                <button onClick={handleSave} disabled={!hasChanges || isSaving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium">
                  {isSaving ? (<><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Speichern...</>)
                    : (<><CheckIcon className="h-4 w-4" /> Speichern</>)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BudgetDetailModal;
